{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2018 - 2019                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.Dialogs;

interface

uses
  Classes, WEBLib.Controls, Web, SysUtils, WEBLib.ExtCtrls, WebLib.WebTools, WEBLib.WebCtrls;

type
  TMsgDlgType = (mtWarning, mtError, mtInformation, mtConfirmation, mtCustom);
  TMsgDlgBtn = (mbYes, mbNo, mbOK, mbCancel, mbAbort, mbRetry, mbIgnore,
    mbAll, mbNoToAll, mbYesToAll, mbHelp, mbClose);
  TMsgDlgButtons = set of TMsgDlgBtn;

  TDialogResult = Integer;
  TDialogResultProc = reference to procedure(AValue: TDialogResult);

  TCustomDialogButton = class(TCollectionItem)
  private
    FCaption: string;
    FTag: integer;
    FElementClassName: TElementClassName;
  published
    property Caption: string read FCaption write FCaption;
    property ElementClassName: TElementClassName read FElementClassName write FElementClassName;
    property Tag: integer read FTag write FTag default 0;
  end;

  TCustomDialogButtons = class(TOwnedCollection)
  private
    function GetItem(AIndex: integer): TCustomDialogButton; reintroduce;
    procedure SetItem(AIndex: integer; const Value: TCustomDialogButton); reintroduce;
  public
    function Add: TCustomDialogButton; reintroduce;
    function Insert(AIndex: integer): TCustomDialogButton; reintroduce;
    property Items[AIndex: integer]: TCustomDialogButton read GetItem write SetItem; default;
  end;

  TMessageDlg = class(TCustomControl)
  private
    FOpacity: double;
    FLayer: TJSElement;
    FDlg: TJSElement;
    FCancel: TJSElement;
    FMessage: string;
    FTitle: string;
    FMsgDlgType: TMSgDlgType;
    FOnButtonClick: TNotifyEvent;
    FOnClose: TNotifyEvent;
    FButtons: TMsgDlgButtons;
    FDialogProc: TDialogResultProc;
    FDialogResult: TDialogResult;
    FMdx, FMdy: integer;
    FDlgX, FDlgY: integer;
    FCaptured: boolean;
    FDown: boolean;
    FElementTitleClassName: TElementClassName;
    FElementButtonClassName: TElementClassName;
    FElementContentClassName: TElementClassName;
    FDialogText: TStringList;
    FCustomButtons: TCustomDialogButtons;
    FElementDialogClassName: TElementClassName;
    procedure SetDialogText(const Value: TStringList);
    procedure SetCustomButtons(const Value: TCustomDialogButtons);
  protected
    function CreateElement: TJSElement; override;
    function GetDialogText(Index: integer): string; virtual;
    procedure BindEvents; override;
    procedure SetDialogResult(const Value: TDialogResult);
    function CreateButton(Caption: string; var DoFocus: boolean; AClass: string = ''): TJSHTMLElement;
    function HandleDocMouseMove(Event: TJSMouseEvent): Boolean; virtual;
    function HandleDocMouseUp(Event: TJSMouseEvent): Boolean; virtual;
    function HandleMouseDown(Event: TJSMouseEvent): Boolean; virtual;
    function HandleButtonClick(Event: TJSMouseEvent): Boolean; virtual;
    function HandleKeyDown(Event: TJSKeyBoardEvent): Boolean; virtual;
    procedure InitBootstrapStyle;
  public
    procedure CreateInitialize; override;
    destructor Destroy; override;
    procedure Close;
    property DialogResult: TDialogResult read FDialogResult write SetDialogResult;
    procedure Show;
    function ShowDialog: Integer; overload; virtual;
    function ShowDialog(Msg: string; DlgType: TMsgDlgType; Buttons: TMsgDlgButtons; AProc: TDialogResultProc = nil): TDialogResult; overload; virtual;
  published
    property Buttons: TMsgDlgButtons read FButtons write FButtons default [];
    property CustomButtons: TCustomDialogButtons read FCustomButtons write SetCustomButtons;
    property DialogText: TStringList read FDialogText write SetDialogText;
    property DialogType: TMSgDlgType read FMsgDlgType write FMsgDlgType default mtCustom;
    property &Message: string read FMessage write FMessage;
    property Opacity: double read FOpacity write FOpacity;
    property ElementButtonClassName: TElementClassName read FElementButtonClassName write FElementButtonClassName;
    property ElementDialogClassName: TElementClassName read FElementDialogClassName write FElementDialogClassName;
    property ElementTitleClassName: TElementClassName read FElementTitleClassName write FElementTitleClassName;
    property ElementContentClassName: TElementClassName read FElementContentClassName write FElementContentClassName;
    property Title: string read FTitle write FTitle;
    property OnButtonClick: TNotifyEvent read FOnButtonClick write FOnButtonClick;
    property OnClose: TNotifyEvent read FOnClose write FOnClose;
  end;

  TWebMessageDlg = class(TMessageDlg);

  TWaitMessage = class(TCustomControl)
  private
    FLayer: TJSElement;
    FPicture: TURLPicture;
    FOpacity: double;
    FOnShow: TNotifyEvent;
    FIsWaiting: boolean;
    procedure SetPicture(const Value: TURLPicture);
  protected
    procedure WaitDisplayed;
    function CreateElement: TJSElement; override;
  public
    procedure CreateInitialize; override;
    destructor Destroy; override;
    procedure Show;
    procedure Hide;
    property IsWaiting: boolean read FIsWaiting write FIsWaiting;
    property Showing: boolean read FIsWaiting;
  published
    property Opacity: double read FOpacity write FOpacity;
    property Picture: TURLPicture read FPicture write SetPicture;
    property OnShow: TNotifyEvent read FOnShow write FOnShow;
  end;

  TWebWaitMessage = class(TWaitMessage);

  TOpenDialogProc = reference to procedure(AFileName: string);

  TOpenDialog = class(TComponent)
  private
    FFiles: TFiles;
    FOnChange: TNotifyEvent;
    FFileName: string;
    FMultiFile: boolean;
    FAccept: string;
    FOpenDialogProc: TOpenDialogProc;
  protected
    procedure HandleInputChange(Event: TJSEvent); virtual;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
    procedure Execute; overload;
    procedure Execute(AProc: TOpenDialogProc); overload;
    property FileName: string read FFileName;
    property Files: TFiles read FFiles;
  published
    property Accept: string read FAccept write FAccept;
    property MultiFile: boolean read FMultiFile write FMultiFile default false;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  end;

  TWebOpenDialog = class(TOpenDialog);


procedure MessageDlg(const Msg: string; DlgType: TMsgDlgType; Buttons: TMsgDlgButtons;  AResultProc: TDialogResultProc = nil);
procedure ShowMessage(AMsg: string);
function InputBox(const ACaption, APrompt, ADefault: string): string;
function InputQuery(const ACaption, APrompt: string; var Value: string): Boolean; overload;
function Confirm(const Value: string): boolean;

implementation

uses
  WEBLib.Forms, WEBLib.Graphics, WebLib.Consts, JS;

var
  WebLibDlg: TMessageDlg;

function StringToHTML(AValue: string): string;
begin
  Result := StringReplace(AValue,#13,'<BR>',[rfReplaceAll]);
  Result := StringReplace(Result,#10,'',[rfReplaceAll]);
end;

procedure ShowMessage(AMsg: string);
begin
  if (Application <> nil) and (Application.MainForm <> nil) and (Application.MainForm.CSSLibrary = cssBootstrap) then
  begin
    MessageDlg(AMsg, mtInformation, [mbOK]);
  end
  else
  begin
    asm
      alert(AMsg);
    end;
  end;
end;

function InputBox(const ACaption, APrompt, ADefault: string): string;
var
  retvalue: string;
begin
//
  asm
    retvalue = prompt(APrompt, ADefault);
    if (retvalue == null) {
      retvalue = "";
    }
  end;

  Result := retvalue;
end;

function InputQuery(const ACaption, APrompt: string; var Value: string): Boolean; overload;
var
  retvalue: boolean;
  s: string;
begin
  retvalue := true;
  s := Value;

  asm
    var text = s;
    s = prompt(APrompt, text);
    if (s == null) {
      retvalue = false;
    }
  end;

  if retvalue then
    Value := s;

  Result := retvalue;
end;

function Confirm(const Value: string): boolean;
var
  res: boolean;
begin
  asm
    res = confirm(Value);
  end;
  Result := res;
end;

procedure MessageDlg(const Msg: string; DlgType: TMsgDlgType; Buttons: TMsgDlgButtons;  AResultProc: TDialogResultProc = nil);
begin
  if not Assigned(WebLibDlg) then
  begin
    WebLibDlg := TMessageDlg.Create(nil);
    if Application.MainForm.CSSLibrary = cssBootstrap then
    begin
      TMessageDlg(WebLibDlg).InitBootstrapStyle;
    end;
  end;

  WebLibDlg.ShowDialog(Msg, DlgType, Buttons, AResultProc);
end;

{ TMessageDlg }

procedure TMessageDlg.CreateInitialize;
begin
  inherited;
  FOpacity := 0.2;
  FMessage := '';
  FMsgDlgType := mtInformation;
  FDialogText := TStringList.Create;
  FDialogText.Add(SWarning); // 0
  FDialogText.Add(SError);   // 1
  FDialogText.Add(SInformation); // 2
  FDialogText.Add(SConfirm);     // 3
  FDialogText.Add('Custom');      // 4
  FDialogText.Add(SOK);          // 5
  FDialogText.Add(SCancel);      // 6
  FDialogText.Add(SYes);         // 7
  FDialogText.Add(SNo);          // 8
  FDialogText.Add(SAbort);       // 9
  FDialogText.Add(SRetry);       // 10
  FDialogText.Add(SIgnore);      // 11
  FDialogText.Add(SAll);         // 12
  FDialogText.Add(SYestoAll);  // 13
  FDialogText.Add(SNotoAll);   // 14
  FDialogText.Add(SHelp);        // 15
  FDialogText.Add(SClose);       // 16

  FMsgDlgType := mtCustom;

  FCustomButtons := TCustomDialogButtons.Create(Self, TCustomDialogButton);
  FCustomButtons.PropName := 'CustomButtons';
end;

destructor TMessageDlg.Destroy;
begin
  FDialogText.Free;
  FCustomButtons.Free;
  inherited;
end;

function TMessageDlg.GetDialogText(Index: integer): string;
begin
  Result := 'Undefined';

  if Index < FDialogText.Count then
    Result := FDialogText.Strings[Index];
end;

procedure TMessageDlg.BindEvents;
begin
//
end;

function TMessageDlg.ShowDialog: integer;
begin
  Result := mrNone;
  Show;
end;

function TMessageDlg.ShowDialog(Msg: string; DlgType: TMsgDlgType; Buttons: TMsgDlgButtons; AProc: TDialogResultProc = nil): TDialogResult;
begin
  Result := mrNone;
  FButtons := Buttons;
  FMessage := Msg;
  FMsgDlgType := DlgType;
  FDialogProc := AProc;
  DialogResult := mrNone;
  Show;
end;

function TMessageDlg.CreateButton(Caption: string; var DoFocus: boolean; AClass: string = ''): TJSHTMLElement;
begin
  Result := TJSHTMLElement(document.createElement('BUTTON'));
  Result.innerHTML := Caption;

  if Application.MainForm.CSSLibrary = cssBootstrap then
  begin
    Result['type'] := 'BUTTON';
    Result['class'] := 'btn ' + AClass;
    Result['data-dismiss'] := 'modal-dialog';
  end
  else
  begin
    Result.style.setProperty('width', '100px');
    Result.style.setProperty('margin-left', '5px');
    if ElementButtonClassName <> '' then
      Result['class'] := ElementButtonClassName + ' ' + AClass
    else
      if Application.ThemeButtonClassName <> '' then
        Result['class'] := Application.ThemeButtonClassName + ' ' + AClass;
  end;

  Result.id := Caption;
  Result.addEventListener('click', @HandleButtonClick);
  Result.addEventListener('keydown', @HandleKeyDown);

  if DoFocus then
  begin
    asm
      setTimeout(function() {Result.focus();}, 1);
    end;
    DoFocus := false;
  end;
end;

function TMessageDlg.CreateElement: TJSElement;
begin
  if (csDesigning in ComponentState) then
    Result := nil
  else
    inherited;
end;

procedure TMessageDlg.Show;
var
  title, msg, icon, content, contentdiv, bar, edlg, eh, cancel: TJSHTMLElement;
  adiv,ldiv,cdiv: TJSElement;
  MsgSymbol, MsgColor, MsgTitle, BorderColor, BarBGColor, BarHeight, Padding: string;
  btnFocus: boolean;
  i: Integer;

begin
  if (Application = nil) or (Application.MainForm = nil) then
    Exit;

  Application.LockForm(Application.MainForm);

  FLayer := document.createElement('SPAN');
  document.body.appendChild(FLayer);
  FLayer['tabindex'] := '0';
  FLayer.addEventListener('keydown', @HandleKeyDown);

  btnFocus := true;
  MsgTitle := FTitle;
  FMessage := StringToHTML(FMessage);

  case FMsgDlgType of
    mtWarning: begin MsgSymbol := 'warning'; MsgColor := 'orange'; MsgTitle := GetDialogText(0); end;
    mtError: begin MsgSymbol := 'cancel'; MsgColor := 'red'; MsgTitle := GetDialogText(1); end;
    mtInformation: begin MsgSymbol := 'info'; MsgColor := 'blue'; MsgTitle := GetDialogText(2); end;
    mtConfirmation: begin MsgSymbol := 'help'; MsgColor := 'blue'; MsgTitle := GetDialogText(3); end;
    mtCustom: begin MsgSymbol := ''; MsgColor := ''; MsgTitle := GetDialogText(4); end;
  end;

  if Application.MainForm.CSSLibrary = cssBootstrap then
  begin
    FDlg := document.createElement('DIV');
    FDlg['class'] := 'modal-dialog'; //  modal-dialog-centered';
    FDlg['role'] := 'document';

    TJSHTMLElement(FDlg).style.setProperty('z-index', '19998');

    // insert opaque background on entire window
    eh := TJSHTMLElement(document.createElement('SPAN'));
    eh.style.setProperty('position', 'absolute');
    eh.style.setProperty('top', '0');
    eh.style.setProperty('left', '0');
    eh.style.setProperty('right', '0');
    eh.style.setProperty('bottom', '0');
    eh.style.setProperty('z-index', '19997');
    document.body.appendChild(eh);
    eh.appendChild(FDlg);

    ldiv := FDlg;

    adiv := document.createElement('DIV');
    adiv['class'] := 'modal-content ' + ElementDialogClassName;
    ldiv.appendChild(adiv);

    cdiv := adiv;
    ldiv := adiv;

    adiv := document.createElement('DIV');
    adiv['class'] := 'modal-header';
    cdiv.appendChild(adiv);

    ldiv := adiv;

    adiv := document.createElement('H5');
    adiv['class'] := 'modal-title';
    adiv.innerHTML := MsgTitle;

    ldiv.appendChild(adiv);

    if (mbCancel in Buttons) then
    begin
      adiv := document.createElement('BUTTON');
      adiv['type'] := 'button';
      adiv['class'] := 'close';
      adiv['data-dismiss'] := 'modal-dialog';
      adiv['aria-label'] := 'Close';
      adiv['id'] := GetDialogText(6);
      adiv.addEventListener('click', @HandleButtonClick);

      ldiv.appendChild(adiv);
      ldiv := adiv;

      adiv := document.createElement('SPAN');
      adiv['aria-hidden'] := 'true';
      adiv.innerHTML := '&times;';

      ldiv.appendChild(adiv);
    end;

    adiv := document.createElement('DIV');
    adiv['class'] := 'modal-body';
    cdiv.appendChild(adiv);

    ldiv := adiv;

    if FMsgDlgType <> mtCustom then
    begin
      icon := TJSHTMLElement(document.createElement('SPAN'));
      icon.innerHTML := '<i class="material-icons" style="color:' + MsgColor + '!important;font-size:48px!important">' + MsgSymbol + '</i>';
      icon.style.setProperty('display', 'inline-block');
      icon.style.setProperty('float', 'left');
      icon.style.setProperty('padding-right', '15px');
      ldiv.appendChild(icon);
    end;

    adiv := document.createElement('P');
    adiv.innerHTML := FMessage;

    ldiv.appendChild(adiv);

    adiv := document.createElement('DIV');
    adiv['class'] := 'modal-footer';
    cdiv.appendChild(adiv);

    ldiv := adiv;

    bar := TJSHTMLElement(ldiv);

    FDlg := eh;
  end
  else
  begin
    Padding := '5';
    BarHeight := '26';
    BarBGColor := '#f1f1f1';
    BorderColor := '#ababab';

    FDlg := document.createElement('DIV');
    document.body.appendChild(FDlg);

    edlg := TJSHTMLElement(FDlg);

    if ElementDialogClassName <> '' then
     edlg['class'] := ElementDialogClassName;

    edlg.style.setProperty('background-color', 'white');
    edlg.style.setProperty('cursor', 'default');
    edlg.style.setProperty('border', '1px solid ' + BorderColor);
    edlg.style.setProperty('position', 'fixed');
    edlg.style.setProperty('font-family', 'Arial');
    edlg.style.setProperty('font-size', '10pt');
    edlg.style.setProperty('min-width', '300px');
    edlg.style.setProperty('min-height', '125px');
    edlg.style.setProperty('max-width', '100%');
    edlg.style.setProperty('max-height', '100%');
    edlg.style.setProperty('top', '50%');
    edlg.style.setProperty('left', '50%');
    edlg.style.setProperty('transform', 'translate(-50%, -50%)');
    edlg.style.setProperty('z-index', '19998');
    edlg.style.setProperty('box-shadow','5px 5px 5px gray');

    cancel := TJSHTMLElement(document.createElement('DIV'));
    FCancel := cancel;
    cancel.innerHTML := '<i id="Cancel" class="material-icons" style="font-size:16px!important">clear</i>';
    cancel.id := 'Cancel';
    cancel.style.setProperty('color', ColorToHTML(Application.ThemeTextColor));
    cancel.style.setProperty('position', 'fixed');
    cancel.style.setProperty('top', '0');
    cancel.style.setProperty('right', '0');
    cancel.style.setProperty('padding', Padding + 'px');
    cancel.style.setProperty('height', BarHeight + 'px');
    cancel.style.setProperty('line-height', BarHeight + 'px');
    cancel.addEventListener('click', @HandleButtonClick);
    edlg.appendChild(cancel);

    title := TJSHTMLElement(document.createElement('DIV'));
    title.innerHTML := MsgTitle;

    if FElementTitleClassName <> '' then
    begin
      title['class'] := FElementTitleClassName;
    end
    else
    begin
      title.style.setProperty('background-color', ColorToHTML(Application.ThemeColor));
      title.style.setProperty('color', ColorToHTML(Application.ThemeTextColor));
    end;

    title.style.setProperty('cursor', 'move');
    title.style.setProperty('padding-left', Padding + 'px');
    title.style.setProperty('height', BarHeight + 'px');
    title.style.setProperty('vertical-align','middle');
    title.style.setProperty('line-height', BarHeight + 'px');
    title.style.setProperty('border-bottom', '1px solid ' + BorderColor);

    title.style.setProperty('-moz-user-select','none');
    title.style.setProperty('-webkit-user-select','none');
    title.style.setProperty('-ms-user-select','none');
    title.style.setProperty('user-select','none');
    title.style.setProperty('-o-user-select','none');

    title.addEventListener('mousedown',@HandleMouseDown);

    edlg.appendChild(title);

    msg := TJSHTMLElement(document.createElement('DIV'));

    msg.style.setProperty('padding', Padding + 'px');
    msg.style.setProperty('height', 'auto');
    msg.style.setProperty('min-height', '50px');
    msg.style.setProperty('border-bottom', '1px solid ' + BorderColor);

    if FMsgDlgType <> mtCustom then
    begin
      icon := TJSHTMLElement(document.createElement('DIV'));
      icon.innerHTML := '<i class="material-icons" style="color:' + MsgColor + '!important;font-size:48px!important">' + MsgSymbol + '</i>';
      icon.style.setProperty('display', 'inline-block');
      icon.style.setProperty('float', 'left');
      icon.style.setProperty('padding-right', '15px');
      msg.appendChild(icon);
    end;

    FMessage := StringReplace(FMessage, sLineBreak, '<BR>', [rfReplaceAll]);

    contentdiv := TJSHTMLElement(document.createElement('DIV'));
    contentdiv.innerHTML := FMessage;
    contentdiv.style.setProperty('height','auto');
    contentdiv.style.setProperty('min-height','50px');
    contentdiv.style.setProperty('white-space','pre-wrap');

    content := TJSHTMLElement(document.createElement('LABEL'));
    content.innerHTML := FMessage;

    if FElementTitleClassName <> '' then
    begin
      content['class'] := FElementContentClassName;
    end;

    contentdiv.appendChild(content);

    msg.appendChild(content);

    edlg.appendChild(msg);

    bar := TJSHTMLElement(document.createElement('DIV'));
    bar.style.setProperty('background-color', BarBGColor);
    bar.style.setProperty('padding', Padding + 'px');
    bar.style.setProperty('min-height', BarHeight + 'px');
    bar.style.setProperty('height', 'auto');
    bar.style.setProperty('line-height', BarHeight + 'px');
    bar.style.setProperty('text-align', 'right');
    edlg.appendChild(bar);
  end;

  if (FButtons = []) and (CustomButtons.Count = 0) then
    bar.appendChild(CreateButton(GetDialogText(5), btnFocus))
  else
  begin
    if mbYes in FButtons then
      bar.appendChild(CreateButton(GetDialogText(7), btnFocus));

    if mbNo in FButtons then
      bar.appendChild(CreateButton(GetDialogText(8), btnFocus));

    if mbOK in FButtons then
      bar.appendChild(CreateButton(GetDialogText(5), btnFocus));

    if mbCancel in FButtons then
      bar.appendChild(CreateButton(GetDialogText(6), btnFocus));

    if mbAbort in FButtons then
      bar.appendChild(CreateButton(GetDialogText(9), btnFocus));

    if mbRetry in FButtons then
      bar.appendChild(CreateButton(GetDialogText(10), btnFocus));

    if mbIgnore in FButtons then
      bar.appendChild(CreateButton(GetDialogText(11), btnFocus));

    if mbAll in FButtons then
      bar.appendChild(CreateButton(GetDialogText(12), btnFocus));

    if mbNoToAll in FButtons then
      bar.appendChild(CreateButton(GetDialogText(13), btnFocus));

    if mbYesToAll in FButtons then
      bar.appendChild(CreateButton(GetDialogText(14), btnFocus));

    if mbHelp in FButtons then
      bar.appendChild(CreateButton(GetDialogText(15), btnFocus));

    if mbClose in FButtons then
      bar.appendChild(CreateButton(GetDialogText(16), btnFocus));
  end;

  for i := 0 to FCustomButtons.Count - 1 do
  begin
    bar.appendChild(CreateButton(FCustomButtons[i].Caption, btnFocus, FCustomButtons[i].ElementClassName));
  end;

  eh := TJSHTMLElement(FLayer);
  eh.style.setProperty('background-color', 'black');
  eh.style.setProperty('opacity', DoubleToHTML(Opacity));

  eh.style.setProperty('top', '0');
  eh.style.setProperty('left', '0');
  eh.style.setProperty('right', '0');
  eh.style.setProperty('bottom', '0');
  eh.style.setProperty('z-index', '19990');

  eh.style.setProperty('webkit-user-select', 'none');
  eh.style.setProperty('moz-user-select', 'none');
  eh.style.setProperty('khtml-user-select', 'none');
  eh.style.setProperty('ms-user-select', 'none');
  eh.style.setProperty('user-select', 'none');
  eh.style.SetProperty('position','fixed');
end;

procedure TMessageDlg.SetCustomButtons(const Value: TCustomDialogButtons);
begin
  FCustomButtons.Assign(Value);
end;

procedure TMessageDlg.SetDialogResult(const Value: TDialogResult);
begin
  FDialogResult := Value;
  if (FDialogResult <> mrNone) then
    Close;
end;

procedure TMessageDlg.SetDialogText(const Value: TStringList);
begin
  FDialogText.Assign(Value);
end;

procedure TMessageDlg.Close;
begin
  Application.UnlockForm(Application.MainForm);

  document.body.removeChild(FLayer);
  document.body.removeChild(FDlg);

  FDown := false;
  FCaptured := false;

  if Assigned(OnClose) then
    OnClose(Self);

  if Assigned(FDialogProc) then
    FDialogProc(FDialogResult);
end;

function TMessageDlg.HandleButtonClick(Event: TJSMouseEvent): Boolean;
var
  id: string;
  i: integer;
begin
  DialogResult := mrNone;

  id := event.target['id'];

  if id = GetDialogText(7) then
    DialogResult := mrYes
  else if id = GetDialogText(8) then
    DialogResult := mrNo
  else if id = GetDialogText(5) then
    DialogResult := mrOK
  else if id = GetDialogText(6) then
    DialogResult := mrCancel
  else if id = GetDialogText(9) then
    DialogResult := mrAbort
  else if id = GetDialogText(10) then
    DialogResult := mrRetry
  else if id = GetDialogText(11) then
    DialogResult := mrIgnore
  else if id = GetDialogText(12) then
    DialogResult := mrAll
  else if id = GetDialogText(13) then
    DialogResult := mrNoToAll
  else if id = GetDialogText(14) then
    DialogResult := mrYesToAll
  else if id = GetDialogText(15) then
    DialogResult := mrHelp
  else if id = GetDialogText(16) then
    DialogResult := mrClose;

  if (DialogResult = mrNone) and (CustomButtons.Count > 0) then
  begin
    for i := 0 to CustomButtons.Count - 1 do
    begin
      if id = CustomButtons[i].Caption then
      begin
        DialogResult := 100 + i;
        break;
      end;

    end;
  end;

  if Assigned(OnButtonClick) then
    OnButtonClick(Self);

  Result := true;
end;

function TMessageDlg.HandleDocMouseMove(Event: TJSMouseEvent): Boolean;
var
  deltax,deltay: double;
  el: TJSHTMLElement;
begin
  if FDown then
  begin
    deltax := Event.screenX - FMdx;
    deltay := Event.screenY - FMdy;

    el := TJSHTMLElement(FDlg);

    el.style.setProperty('transform','');
    el.style.setProperty('position','absolute');
    el.style.setProperty('left', inttostr(round(FDlgX + deltax))+'px');
    el.style.setProperty('top', inttostr(round(FDlgY + deltay))+'px');

    el := TJSHTMLElement(FCancel);
    el.style.setProperty('position', 'absolute');
    el.style.setProperty('top', '0');
    el.style.setProperty('right', '0');
  end;

  Result := true;
end;

function TMessageDlg.HandleDocMouseUp(Event: TJSMouseEvent): Boolean;
begin
  FDown := false;
  document.body.removeEventListener('mousemove', @HandleDocMouseMove);
  document.body.removeEventListener('mouseup', @HandleDocMouseUp);

  Result := true;
end;

function TMessageDlg.HandleMouseDown(Event: TJSMouseEvent): Boolean;
var
  r: TJSDOMRect;
begin
  FMdx := Round(Event.screenX);
  FMdy := Round(Event.screenY);

  r := FDlg.getBoundingClientRect();

  FDlgX := Round(int(r.left));
  FDlgY := Round(int(r.top));

  FDown := true;

  if not FCaptured then
  begin
    FCaptured := true;
    document.body.addEventListener('mousemove', @HandleDocMouseMove);
    document.body.addEventListener('mouseup', @HandleDocMouseUp);
  end;

  Result := true;
end;

procedure TMessageDlg.InitBootstrapStyle;
begin
  FElementButtonClassName := 'btn';
  FElementContentClassName := 'text-body';
  FElementTitleClassName := 'text-body';
  FElementDialogClassName := 'shadow-lg p-3 mb-5 bg-white rounded';
end;

function TMessageDlg.HandleKeyDown(Event: TJSKeyBoardEvent): Boolean;
var
  k: Word;
begin
  k := GetKeyCode(Event.Key);
  if not Assigned(k) then
    Exit;
  if k = VK_ESCAPE then
    DialogResult := mrCancel;
  Result := true;
end;

{ TWaitMessage }

function TWaitMessage.CreateElement: TJSElement;
begin
  if (csDesigning in ComponentState) then
    Result := nil
  else
    inherited;
end;

procedure TWaitMessage.CreateInitialize;
begin
  inherited;
  FOpacity := 0.2;
  FPicture := TURLPicture.Create;
  FIsWaiting := false;
end;

destructor TWaitMessage.Destroy;
begin
  FPicture.Free;
  inherited;
end;

procedure TWaitMessage.Hide;
begin
  if FIsWaiting then
    document.body.removeChild(FLayer);
  FIsWaiting := false;
end;

procedure TWaitMessage.SetPicture(const Value: TURLPicture);
begin
  FPicture.Assign(Value);
end;

procedure TWaitMessage.Show;
var
  eh,img,dv: TJSHTMLElement;
  el: TJSElement;
begin
  FLayer := document.createElement('SPAN');

  eh := TJSHTMLElement(FLayer);
  eh.style.setProperty('background-color', 'black');
  eh.style.setProperty('opacity', DoubleToHTML(Opacity));
  eh.style.setProperty('top', '0');
  eh.style.setProperty('left', '0');
  eh.style.setProperty('right', '0');
  eh.style.setProperty('bottom', '0');
  eh.style.setProperty('z-index', '19999');

  eh.style.setProperty('webkit-user-select', 'none');
  eh.style.setProperty('moz-user-select', 'none');
  eh.style.setProperty('khtml-user-select', 'none');
  eh.style.setProperty('ms-user-select', 'none');
  eh.style.setProperty('user-select', 'none');
  eh.style.SetProperty('position','fixed');

  document.body.appendChild(FLayer);
  FLayer['tabindex'] := '0';

  el := document.createElement('DIV');
  dv := TJSHTMLElement(el);
  FLayer.appendChild(el);
  dv.style.SetProperty('position','relative');
  dv.style.SetProperty('width','100%');
  dv.style.SetProperty('height','100%');

  el := document.createElement('IMG');
  img := TJSHTMLElement(el);
  dv.appendChild(img);
  img['src'] := FPicture.FileName;
  img.style.setProperty('position','absolute');
  img.style.setProperty('top', '0');
  img.style.setProperty('left', '0');
  img.style.setProperty('right', '0');
  img.style.setProperty('bottom', '0');
  img.style.setProperty('margin', 'auto');

  FIsWaiting := true;

  if Assigned(OnShow) then
  begin
    window.setTimeout(@WaitDisplayed, 100);
  end;
end;

procedure TWaitMessage.WaitDisplayed;
begin
  if Assigned(OnShow) then
    OnShow(Self);
end;

{ TCustomDialogButtons }

function TCustomDialogButtons.Add: TCustomDialogButton;
begin
  Result := TCustomDialogButton(inherited Add);
end;

function TCustomDialogButtons.GetItem(AIndex: integer): TCustomDialogButton;
begin
  Result := TCustomDialogButton(inherited Items[AIndex]);
end;

function TCustomDialogButtons.Insert(AIndex: integer): TCustomDialogButton;
begin
  Result := TCustomDialogButton(inherited Insert(AIndex));
end;

procedure TCustomDialogButtons.SetItem(AIndex: integer;
  const Value: TCustomDialogButton);
begin
  inherited Items[AIndex] := Value;
end;

{ TOpenDialog }

constructor TOpenDialog.Create(AOwner: TComponent);
begin
  inherited Create(AOwner);
  FFiles := TFiles.Create(Self);
end;

destructor TOpenDialog.Destroy;
begin
  FFiles.Free;
  inherited;
end;

procedure TOpenDialog.Execute(AProc: TOpenDialogProc);
begin
  FOpenDialogProc := AProc;
  Execute;
end;

{$HINTS OFF}
procedure TOpenDialog.Execute;
var
  ic: pointer;
  fp: TJSHTMLElement;
begin
  ic := @HandleInputChange;
  fp := TJSHTMLElement(document.createElement('input'));
  fp.setAttribute('type','file');
  if FMultiFile then
    fp.setAttribute('multiple','');

  if FAccept <> '' then
    fp.SetAttribute('accept',FAccept);

  fp.addEventListener('change', ic);

  asm
    fp.click();
  end;
end;
{$HINTS ON}

procedure TOpenDialog.HandleInputChange(Event: TJSEvent);
var
  i,l,sz: integer;
  s,m: string;
  f: TFile;
  d: TJSDate;
  jsfile: TJSHTMLFile;

begin
  // clear existing collection
  while FFiles.Count > 0 do
    FFiles.Delete(0);

  asm
    var curFiles = Event.target.files;
    l = curFiles.length;
  end;

  for i := 0 to l - 1 do
  begin
    f := FFiles.Add;

    asm
      jsfile = curFiles[i];
      s = curFiles[i].name;
      m = curFiles[i].type;
      sz = curFiles[i].size;
      d = new Date(curFiles[i].lastModified);
    end;

    // get the first file
    if (i = 0) then
      FFileName := s;

    f.FileObject := jsfile;
    f.Name := s;
    f.MimeType := m;
    f.Size := sz;
    f.Modified := EncodeDate(d.Year + 1900, d.Month + 1, d.Date)+EncodeTime(d.Hours, d.Minutes, d.Seconds, d.Milliseconds);
  end;

  if Assigned(FOpenDialogProc) then
    FOpenDialogProc(FFileName);

  FOpenDialogProc := nil;

  if Assigned(OnChange) then
    OnChange(Self);
end;

initialization
  WebLibDlg := nil;

end.